// Components
import { styles, TableSorting } from "components";

// Third-Party Libraries
import { Document, Page, Text, Image, View, Font } from "@react-pdf/renderer";
import JsBarcode from "jsbarcode";

// Utils
import { convertNumber } from "utils";
import { jwtDecode } from "jwt-decode";
import moment from "moment";
import { Fragment } from "react";

// FontAwesome for checkmark
Font.register({
  family: "FontAwesome",
  src: "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/fonts/fontawesome-webfont.ttf",
});

export function ExportPdfItemGroup(params: { data: any; tableActive: TableSorting[] }) {
  const title = "REGISTER GROUP";
  const date = moment().format("DD MMM YYYY");
  const userData: { company_name?: string } = jwtDecode(localStorage.getItem("token") ?? "");
  const companyName = userData.company_name ?? "-";
  const sortedColumns = params.tableActive.filter((c) => c.isActive !== false);

  const columns: { header: string; title: string; width: number, value?: string }[] = [ 
    { header: "NO", title: "NO", width: 3 },
    ...sortedColumns.map((col) => {
      const rendered = renderColumn(col.value!);
      return { header: rendered.header, title: col.title, width: rendered.width, value: col.value };
    }),
  ];

  return (
    <Document>
      <Page style={styles.page} size={"A4"}>
        <View fixed style={styles.fixedTop}>
          <Text style={styles.title}>INVENTORY</Text>
          <Text style={styles.title}>{title}</Text>
          <Text style={styles.date}>{companyName}</Text>
          <Text style={styles.date}>Date: {date}</Text>
        </View>

        <View style={{ height: 20 }} />

        {/* Table Header */}
        <View style={styles.tableHeader}>
          {columns.map((col, index) => (
            <View key={index} style={[styles.tableCol, { flex: col.width }]}>
              <Text style={styles.headerText}>{col.header}</Text>
            </View>
          ))}
        </View>

        {/* Table Body */}
        {params.data.map((item: any, rowIndex: number) => (
          <View key={rowIndex} style={styles.tableRow}>
            {columns.map((col, colIndex) => {
              if (col.title === "NO") {
                return (
                  <View key={colIndex} style={[styles.tableCol, { flex: col.width }]}>
                    <Text style={styles.tableCell}>{rowIndex + 1}</Text>
                  </View>
                );
              }

              return (
                <Fragment key={colIndex}>
                  {renderCell(col.value!, item, col.width)}
                </Fragment>
              );
            })}
          </View>
        ))}

        {/* Footer */}
        <View fixed style={styles.fixed}>
          <Text style={styles.text}>
            {title} | {companyName} | {date}
          </Text>
          <Text
            style={styles.text}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          />
        </View>
      </Page>
    </Document>
  );
}

const generateBarcode = (value: string) => {
  const canvas = document.createElement("canvas");
  JsBarcode(canvas, value, { format: "CODE128" });
  return canvas.toDataURL("image/png");
};

function renderColumn(title: string) {
  switch (title) {
    case "bsi":
      return { header: "BSI", width: 5 };
    case "build":
      return { header: "BUILD", width: 5 };
    case "code":
      return { header: "ITEM CODE", width: 10 };
    case "name":
      return { header: "ITEM NAME", width: 20 }; 
    case "category":
      return { header: "CATEGORY", width: 20 }; 
    case "average_cost":
      return { header: "AVERAGE COST", width: 15 };
    case "last_price":
      return { header: "LAST PRICE", width: 15 };
    case "sell_price":
      return { header: "SELLING PRICE", width: 15 };
    case "soh":
      return { header: "SOH", width: 10 };
    case "unit":
      return { header: "UNIT", width: 8 };
    default:
      return { header: "", width: 0 };
  }
}

function renderCell(title: string, item: any, flex: number) {
  switch (title) {
    case "bsi":
      return (
        <View style={[styles.tableCol, { flex }]}>
          <Text style={styles.tableCell}>{`${item.buy ? "B" : ""}${item.sell ? "S" : ""}${item.inventory ? "I" : ""}`}</Text>
        </View>
      );
    case "build":
      return (
        <View style={[styles.tableCol, { flex }]}>
          <Text style={{ fontFamily: "FontAwesome", fontSize: 10, textAlign: "center" }}>
            {item.build_item ? "\uf00c" : ""}
          </Text>
        </View>
      );
    case "code":
      return (
        <View style={[styles.tableCol, { flex, justifyContent: "center", alignItems: "center" }]}>
          <Image src={generateBarcode(item.product_barcode)} style={styles.image} />
        </View>
      );
    case "name":
      return (
        <View style={[styles.tableCol, { flex }]}>
          <Text style={styles.tableCell}>{item.product_name}</Text>
        </View>
      );
    case "average_cost":
      return (
        <View style={[styles.tableCol, { flex }]}>
          <Text style={[styles.tableCell, { textAlign: "right" }]}>
            {item.avg_price ? convertNumber(parseFloat(item.avg_price)).intoCurrency : "0"}
          </Text>
        </View>
      );
    case "last_price":
      return (
        <View style={[styles.tableCol, { flex }]}>
          <Text style={[styles.tableCell, { textAlign: "right" }]}>
            {!item.build_item && item.last_price
              ? convertNumber(parseFloat(item.last_price)).intoCurrency
              : "0"}
          </Text>
        </View>
      );
    case "sell_price":
      return (
        <View style={[styles.tableCol, { flex }]}>
          <Text style={[styles.tableCell, { textAlign: "right" }]}>
            {!item.build_item && item.sell_price
              ? convertNumber(parseFloat(item.sell_price)).intoCurrency
              : "0"}
          </Text>
        </View>
      );
    case "soh":
      return (
        <View style={[styles.tableCol, { flex,  }]}>
          <Text style={[styles.tableCell, { textAlign: "center" }]}>{item.soh ?? "-"}</Text>
        </View>
      );
    case "unit":
      return (
        <View style={[styles.tableCol, { flex }]}>
          <Text style={styles.tableCell}>{item.unit_convention}</Text>
        </View>
      );
    default:
      return (
        <View></View>
      );
  }
}
